import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { LanguageService } from '@klickdata/core/localization';
import { MediaAttachmentComponent } from '@klickdata/core/media/src/media-attachment/media-attachment.component';
import { Media, MediaData } from '@klickdata/core/media/src/media.model';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { S3MediaService } from '@klickdata/core/media/src/s3-media.service';
import { SideNaveActionsTypes, SideNaveData, SideNaveResponseData } from '@klickdata/core/mobile/src/mobile.service';
import { AppScope } from '@klickdata/core/resource';
import { MaterialModelType } from '@klickdata/core/resource/src/resource.model';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { ConfigService } from '@klickdata/core/config';
import { Utils } from '@klickdata/core/util';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { User } from '@klickdata/core/user';
import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { YouTubeVideoData } from '../materials-manager-core/materials-manager-link/materials-manager-link.component';
import { HttpClient } from '@angular/common/http';
import { TagService } from '@klickdata/core/resource/src/tag/tag.service';
import moment from 'moment';
import { Clipboard } from '@angular/cdk/clipboard';

@Component({
    selector: 'app-menu-side-media-ai-create-update',
    templateUrl: './menu-side-media-ai-create-update.component.html',
    styleUrls: ['./menu-side-media-ai-create-update.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuSideMediaAiCreateUpdateComponent extends OnDestroyHandler implements OnInit {
    @Input() public navData: SideNaveData;
    @Output() onClose: EventEmitter<SideNaveResponseData> = new EventEmitter();
    @ViewChild('uploader') uploader: MediaAttachmentComponent;
    @ViewChild('prompt') prompt: ElementRef<HTMLTextAreaElement>;
    public mediaForm: FormGroup;
    public mediaSource = new FormControl('upload');
    public urlSource = new FormControl(null);
    public media: Media;
    public authUser$: Observable<User>;
    AppScope = AppScope;
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    public currentLaguageId$: Observable<number>;
    public advancedModeSub: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    MaterialModelType = MaterialModelType;
    onSavingMedia: EventEmitter<boolean> = new EventEmitter<boolean>();
    public progress$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    @ViewChild('input') input: ElementRef<HTMLInputElement>;
    public mediaItem$: Observable<Media>;
    private aiProcessingMsg: string = $localize`Video creation process takes a few minutes, It'll be added to your list soon.`;
    Utils = Utils;
    public youTubeVideoInfo$: Observable<YouTubeVideoData | null>;
    isLoadingVideoInfo: boolean;
    public isVideoUrl$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        protected fb: FormBuilder,
        protected langService: LanguageService,
        protected mediaService: MediaService,
        protected authService: AuthService,
        protected s3Service: S3MediaService,
        protected messageService: MessageService,
        protected config: ConfigService,
        protected http: HttpClient,
        protected tagsService: TagService,
        protected clipboard: Clipboard
    ) {
        super();
        this.mediaForm = this.fb.group({
            id: [],
            name: [null],
            description: [null],
            // priority: ['a3'],
            visibility: ['public'],
            tag_ids: [[]],
            prompt: [''],
        });
    }
    ngOnInit(): void {
        this.authUser$ = this.authService.getUser();
        this.currentLaguageId$ = this.langService
            .getLanguageByKey(this.langService.getCurrentLanguage().value)
            .pipe(map((lang) => lang.id));
        if (this.navData.data.media) {
            this.media = this.navData.data.media;
            this.mediaForm.patchValue(this.media);
            this.mediaForm.get('name').patchValue(this.media.filename);
            this.mediaItem$ = this.mediaService.getMediaByIds([this.mediaForm.value.id]).pipe(map((media) => media[0]));
            if (!!Utils.isVideoUrl(this.media.url || this.media.src || this.media.link)) {
                this.mediaSource.patchValue('url');
                this.isVideoUrl$.next(true);
                this.urlSource.patchValue(this.media.url || this.media.src || this.media.link);
                this.getLinkInfo(this.urlSource.value);
            }
        }
        if (this.navData.data.aiProcessingMsg) {
            this.aiProcessingMsg = this.navData.data.aiProcessingMsg;
        }
        this.mediaSource.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
            if (value === 'ai') {
                setTimeout(() => {
                    this.prompt.nativeElement.focus();
                }, 400);
            }
        });
        this.urlSource.valueChanges
            .pipe(
                takeUntil(this.destroy),
                debounceTime(300),
                distinctUntilChanged(),
                filter((link) => Utils.isVideoUrl(link)),
                tap((link) => this.getLinkInfo(link))
            )
            .subscribe();
    }
    get mediaKey() {
        const link = this.urlSource.value;
        if (link && Utils.isVideoUrl(link)) {
            this.media = Utils.getMedia(link);
            return link;
        }
        return null;
    }
    public onRemoveMedia() {
        this.mediaForm.get('id').patchValue(null);
        this.mediaForm.get('id').markAsDirty();
        this.progress$.next(null);
        this.input.nativeElement.value = null;
    }
    public prepareFile(event) {
        const fileList: FileList = event.target.files;
        if (fileList.length > 0) {
            this.fileHandler(fileList[0]);
        }
    }
    public fileHandler(file: any) {
        this.onSavingMedia.emit(true);
        this.s3Service
            .uploadMediaToS3(file, this.progress$)
            .pipe(
                takeUntil(this.destroy),
                switchMap((media) =>
                    this.mediaService.uploadMediaByLink({
                        ...media.getData(),
                        scope_id: AppScope.MEDIA,
                    })
                ),
                tap((media) => {
                    this.mediaItem$ = of(media);
                })
            )
            .subscribe(
                (media) => {
                    this.onSavingMedia.emit(false);
                    if (media) {
                        this.media = media;
                        this.mediaForm.get('id').patchValue(media.id);
                        this.mediaForm
                            .get('name')
                            .patchValue(
                                !!this.mediaForm.value.name?.trim()?.length ? this.mediaForm.value.name : media.filename
                            );

                        this.mediaForm
                            .get('visibility')
                            .patchValue(this.mediaForm.value.visibility ?? media.visibility);
                        this.mediaForm.markAsDirty();
                        this.progress$.next(null);
                        this.input.nativeElement.value = null;
                    }
                },
                (err) => {} // TODO on error
            );
    }
    public submit() {
        if (
            (!this.mediaForm.value.id && this.mediaSource.value === 'upload') ||
            (!this.mediaForm.value.prompt && this.mediaSource.value === 'ai') ||
            (!Utils.isVideoUrl(this.urlSource.value) && this.mediaSource.value === 'url')
        ) {
            return;
        }
        this.saving.emit(true);
        this.prepareData()
            .pipe(
                switchMap((data) =>
                    this.mediaSource.value === 'upload'
                        ? this.mediaService.updateMedia(this.mediaForm.value.id, data, ['author', 'tags', 'user_feeds'])
                        : this.mediaSource.value === 'url' && Utils.isVideoUrl(this.urlSource.value)
                        ? this.mediaService.uploadMediaByLink(
                              {
                                  link: this.urlSource.value,
                                  type: 'video/' + Utils.getMediaProvider(this.urlSource.value),
                                  scope_id: AppScope.MEDIA,
                                  name: this.mediaForm.value.name,
                                  description: this.mediaForm.value.description,
                                  tag_ids: this.mediaForm.value.tag_ids,
                                  visibility: this.mediaForm.value.visibility,
                              },
                              ['author', 'tags']
                          )
                        : this.mediaService.createAIMedia(
                              data,
                              this.mediaForm.value.prompt,
                              this.navData.data.aiModelType,
                              ['author', 'tags']
                          )
                ),
                catchError(() => {
                    this.saving.emit(false);
                    this.messageService.openMessage(MessageSavedComponent, $localize`Save failed. Please try again.`);
                    return EMPTY;
                }),
                takeUntil(this.destroy),
                filter((data) => !!data)
            )
            .subscribe((data) => {
                this.saving.emit(false);
                if (this.mediaSource.value === 'ai') {
                    this.messageService.openMessage(MessageSavedComponent, this.aiProcessingMsg);
                }
                this.onClose.emit({
                    action: SideNaveActionsTypes.POSITIVE,
                    data: { value: data, type: this.navData.data.type, mediaSource: this.mediaSource.value },
                });
                this.mediaForm.reset();
            });
    }
    prepareData(): Observable<MediaData> {
        const formData: MediaData = Utils.changedControls(this.mediaForm);
        const data = this.media?.id ? this.media.getPayload(formData) : new Media(formData).getPayload();
        data.name = formData.name;
        if (this.media?.id) {
            data.id = this.media.id;
        }
        return of(data);
    }
    goToLink(url: string | null) {
        if (!url || !Utils.isUrl(url)) {
            return;
        }
        window.open(url, '_blank');
    }
    public copyLink(link: string) {
        this.clipboard.copy(link);
        this.messageService.openMessage(MessageSavedComponent, $localize`Link copied to clipboard`);
    }
    private getLinkInfo(link: string) {
        if (Utils.isYoutubeURL(link)) {
            this.isLoadingVideoInfo = true;
            const apiUrl = `https://www.googleapis.com/youtube/v3/videos?id=${Utils.getYoutubeMedia(link).src}&key=${
                this.config.config.YOUTUBE_DATA_API_KEY
            }&part=snippet,contentDetails`;
            this.youTubeVideoInfo$ = this.http.get<any>(apiUrl).pipe(
                map((res) => {
                    if (res?.items?.length) {
                        return res.items[0];
                    } else {
                        this.isLoadingVideoInfo = false;
                        return null;
                    }
                }),
                filter((res) => !!res),
                switchMap((item) => combineLatest([of(item), this.authService.getCustomer()])),
                switchMap(([item, customer]) =>
                    combineLatest([
                        of(item),
                        item.snippet?.tags && !!item.snippet?.tags?.length
                            ? this.currentLaguageId$.pipe(
                                  switchMap((languageId) =>
                                      this.tagsService
                                          .createManyTags(item.snippet?.tags, languageId, customer.id)
                                          .pipe(map((tags) => tags.map((tag) => tag.id)))
                                  )
                              )
                            : of([]),
                    ])
                ),
                map(([item, tagIds]) => {
                    this.isLoadingVideoInfo = false;
                    this.mediaForm.patchValue({
                        name: this.mediaForm.value.name ?? item.snippet.title,
                        description: this.mediaForm.value.description ?? item.snippet?.description,
                        tag_ids: !!this.mediaForm.value.tag_ids?.length ? this.mediaForm.value.tag_ids : tagIds,
                    });
                    return <YouTubeVideoData>{
                        title: item.snippet.title,
                        duration: moment.duration(item.contentDetails.duration).asSeconds(),
                    };
                })
            );
        } else {
            this.youTubeVideoInfo$ = of(null);
        }
    }
}
